import {
  isAaveV2Type,
  isAaveV3Type,
  isAaveV4Type,
  isCompoundV3Type,
  isEulerType,
  isFluid,
  isGearboxV3,
  isInit,
  isMidnight,
  isTerm,
  isExactly,
  isLiquityFamily,
  isRiver,
  isTeller,
  isTermMax,
  isInverse,
  isCooler,
  isFlyingTulip,
  isLlamaLend,
  isResupply,
  isFraxlend,
  isCurvance,
  isTwyne,
  isDssType,
  isFrankencoin,
  isMorphoType,
  isMultiMarket,
  hasCrossMarginRisk,
  isSiloV2Type,
  isSiloV3Type,
  isYLDR,
  isDolomite,
} from '../../../utils'
import {
  LenderUserQuery,
  organizeUserQueries,
} from '../../../types/lenderTypes'
import { LENDER_MODE_NO_MODE } from '../../../assets'
import { getAaveV2UserDataConverter } from '../aave-v2-type/userCallParse'
import { getAaveV3UserDataConverter } from '../aave-v3-type/userCallParse'
import { getInitUserDataConverter } from '../init/userCallParse'
import { getCompoundV3UserDataConverter } from '../compound-v3/userCallParse'
import { getYldrUserDataConverter } from '../aave-v3-type/userCallParseYldr'
import { getMorphoTypeUserDataConverterWithlens } from '../morpho/userCallParse'
import { getCompoundV2UserDataConverter } from '../compound-v2'
import { getEulerUserDataConverter } from '../euler/userCallParse'
import { getAaveV4UserDataConverter } from '../aave-v4-type/userCallParse'
import { getSiloV2UserDataConverter } from '../silo-v2/userCallParse'
import { getSiloV3UserDataConverter } from '../silo-v3/userCallParse'
import { getFluidUserDataConverter } from '../fluid/userCallParse'
import { getGearboxV3UserDataConverter } from '../gearbox/userCallParse'
import { getDolomiteUserDataConverter } from '../dolomite/userCallParse'
import { getMidnightUserDataConverter } from '../midnight/userCallParse'
import { getTermUserDataConverter } from '../term/userCallParse'
import { getExactlyUserDataConverter } from '../exactly/userCallParse'
import { getLiquityUserDataConverter } from '../liquity/userCallParse'
import { getRiverUserDataConverter } from '../river/userCallParse'
import { getInverseUserDataConverter } from '../inverse/userCallParse'
import { getCoolerUserDataConverter } from '../cooler/userCallParse'
import { getFlyingTulipUserDataConverter } from '../flying-tulip/userCallParse'
import { getLlamaLendUserDataConverter } from '../llamalend/userCallParse'
import { getResupplyUserDataConverter } from '../resupply/userCallParse'
import { getFraxlendUserDataConverter } from '../fraxlend/userCallParse'
import { getCurvanceUserDataConverter } from '../curvance/userCallParse'
import { getTwyneUserDataConverter } from '../twyne/userCallParse'
import { getDssUserDataConverter } from '../dss/userCallParse'
import { getFrankencoinUserDataConverter } from '../frankencoin/userCallParse'
import { getTellerUserDataConverter } from '../teller/userCallParse'
import { getTermMaxUserDataConverter } from '../termmax/userCallParse'
import { isFailedCall } from '../../../utils/multicall'
import { LenderToLenderCrossPoolMeta, UserData } from '../utils/types'
import { validateUserData } from './validate'

function getUserDataConverter(
  lender: string,
  chainId: string,
  account: string,
  params?: any,
  meta?: LenderToLenderCrossPoolMeta,
) {
  if (isAaveV4Type(lender))
    // V4 is single-market post-refactor: each spoke is its own lender key
    // (`AAVE_V4_<HEX>`), so the meta slice is keyed by per-spoke key just
    // like every other single-market lender.
    return getAaveV4UserDataConverter(lender, chainId, account, meta?.[lender])
  if (isYLDR(lender))
    return getYldrUserDataConverter(lender, chainId, account, meta?.[lender])
  if (isAaveV2Type(lender))
    return getAaveV2UserDataConverter(lender, chainId, account, meta?.[lender])
  if (isAaveV3Type(lender))
    return getAaveV3UserDataConverter(lender, chainId, account, meta?.[lender])
  if (isInit(lender))
    return getInitUserDataConverter(lender, chainId, account, meta?.[lender])
  if (isMorphoType(lender))
    return getMorphoTypeUserDataConverterWithlens(
      lender,
      chainId,
      account,
      params,
      meta,
    )
  if (isMidnight(lender))
    return getMidnightUserDataConverter(lender, chainId, account, meta)
  if (isTerm(lender))
    return getTermUserDataConverter(lender, chainId, account, meta)
  if (isExactly(lender))
    return getExactlyUserDataConverter(lender, chainId, account, meta)
  if (isLiquityFamily(lender))
    return getLiquityUserDataConverter(lender, chainId, account, meta)
  if (isRiver(lender))
    return getRiverUserDataConverter(lender, chainId, account, meta)
  if (isTeller(lender))
    return getTellerUserDataConverter(lender, chainId, account, meta)
  if (isTermMax(lender))
    return getTermMaxUserDataConverter(lender, chainId, account, meta)
  if (isInverse(lender))
    return getInverseUserDataConverter(lender, chainId, account, meta)
  // Cooler is single-market (`COOLER` is the market key), so the converter
  // returns the UserData entry directly and takes the per-lender meta slice.
  if (isCooler(lender))
    return getCoolerUserDataConverter(lender, chainId, account, meta?.[lender])
  // Flying Tulip MUST be matched here: the tail of this function deliberately
  // falls through to the Compound V2 converter (the fork-zoo default), and a
  // cross-margin lender fed into it breaks the whole multi-lender parse.
  if (isFlyingTulip(lender))
    return getFlyingTulipUserDataConverter(
      lender,
      chainId,
      account,
      meta?.[lender],
    )
  if (isLlamaLend(lender))
    return getLlamaLendUserDataConverter(lender, chainId, account, meta)
  if (isResupply(lender))
    return getResupplyUserDataConverter(lender, chainId, account, meta)
  if (isFraxlend(lender))
    return getFraxlendUserDataConverter(lender, chainId, account, meta)
  if (isCurvance(lender))
    return getCurvanceUserDataConverter(lender, chainId, account, meta)
  if (isTwyne(lender))
    return getTwyneUserDataConverter(lender, chainId, account, meta)
  if (isDssType(lender))
    return getDssUserDataConverter(lender, chainId, account, meta)
  if (isFrankencoin(lender))
    return getFrankencoinUserDataConverter(lender, chainId, account, meta)
  if (isCompoundV3Type(lender))
    return getCompoundV3UserDataConverter(
      lender,
      chainId,
      account,
      meta?.[lender],
    )
  if (isEulerType(lender))
    return getEulerUserDataConverter(
      lender,
      chainId,
      account,
      meta?.[lender],
      params?.subAccountIndexes,
    )
  // Silo takes the WHOLE meta map, not one lender's slice: its queries are
  // collapsed to a single roster-wide read, and the converter publishes one entry
  // per pair key, so it has to look each pair's meta up itself.
  if (isSiloV2Type(lender))
    return getSiloV2UserDataConverter(lender, chainId, account, meta, params)
  if (isSiloV3Type(lender))
    return getSiloV3UserDataConverter(lender, chainId, account, meta, params)
  if (isFluid(lender))
    return getFluidUserDataConverter(lender, chainId, account, meta)
  if (isGearboxV3(lender))
    return getGearboxV3UserDataConverter(lender, chainId, account, meta)
  if (isDolomite(lender))
    return getDolomiteUserDataConverter(
      lender,
      chainId,
      account,
      meta?.[lender],
    )

  return getCompoundV2UserDataConverter(
    lender,
    chainId,
    account,
    meta?.[lender],
  )
}

/**
 * Checks if a user data entry has any balance (deposits or debt) or positions.
 *
 * `keepActiveControllerMode` (Euler only): also keep an otherwise-empty
 * sub-account when it has an active controller — i.e. `userConfig.selectedMode`
 * is a real vault address rather than the `LENDER_MODE_NO_MODE` sentinel.
 * Leverage-open needs that controller to emit a matching disableController item;
 * dropping the record would hide it. Off for every other lender so existing
 * empty-filtering is unchanged.
 */
const hasBalanceOrPositions = (
  entry: {
    balanceData: { deposits: number; debt: number }
    positions: any[]
    userConfig?: { selectedMode?: string }
  },
  keepActiveControllerMode = false,
): boolean => {
  const hasBalance =
    entry.balanceData.deposits > 0 || entry.balanceData.debt > 0
  const hasPositions = entry.positions && entry.positions.length > 0
  const hasActiveMode =
    keepActiveControllerMode &&
    !!entry.userConfig?.selectedMode &&
    entry.userConfig.selectedMode !== String(LENDER_MODE_NO_MODE)
  return hasBalance || hasPositions || hasActiveMode
}

/**
 * Filters out empty data entries from UserData
 * Keeps entries that have balance (deposits or debt > 0) OR have at least one
 * position — and, when `keepActiveControllerMode` is set (Euler), entries that
 * carry an active controller mode even with no balance/positions.
 */
const filterEmptyUserData = (
  userData: UserData,
  keepActiveControllerMode = false,
): UserData | null => {
  const filteredData = userData.data.filter((e) =>
    hasBalanceOrPositions(e, keepActiveControllerMode),
  )
  if (filteredData.length === 0) return null
  return { ...userData, data: filteredData }
}

/**
 * How a lender's slice reacts to a failed read.
 *
 * Note what this is NOT keyed on: "is the position cross-margin". EVERY position
 * with debt is corrupted by a lost read — an isolated Morpho market computes its
 * health from a collateral read and a debt read, so losing either fabricates the
 * same nonsense a lost Aave reserve does. The question here is narrower and
 * purely mechanical: **what is the smallest thing we can void?** A failed slot
 * carries no market label, so the only unit we can void is the lender key.
 *
 * - `strict` — void the whole slice on any lost read. Correct when the slice
 *   resolves to ONE risk computation, which is true both for a single-market
 *   lender and for a multi-market lender that is nevertheless cross-margin
 *   (Exactly scores every market under one per-chain Auditor). Nothing smaller
 *   can be voided, and publishing the remainder would publish a fiction.
 * - `lenient` — publish, flag `incomplete`, and let per-record invariant
 *   validation catch the corrupted one. Correct ONLY when the slice fans out to
 *   many INDEPENDENT isolated positions, where voiding the key would discard
 *   hundreds of intact markets to hide one — a cure worse than the disease.
 *
 * So leniency requires BOTH properties: many independent positions AND no shared
 * risk computation across them.
 */
export type ReadFailurePolicy = 'strict' | 'lenient'

export const getReadFailurePolicy = (lender: string): ReadFailurePolicy =>
  isMultiMarket(lender) && !hasCrossMarginRisk(lender) ? 'lenient' : 'strict'

/** Why a lender's slice did not convert cleanly. */
export type IncompleteReason =
  /** Every read in the slice failed. */
  | 'all-reads-failed'
  /** Reads failed on a strict lender — the whole risk set was voided. */
  | 'partial-read-cross-margin'
  /** Reads failed on a lenient lender — surviving markets were published. */
  | 'partial-read'
  /** The converter threw. */
  | 'converter-error'
  /** The converted entry asserted something that cannot be true. */
  | 'invariant-violation'

/** Reported per lender whose multicall slice did not convert cleanly. */
export interface IncompleteLenderRead {
  lender: string
  /** Number of slots in the lender's slice that returned no data. */
  failedCalls: number
  /**
   * Subset of `failedCalls` that could plausibly succeed on a re-read — i.e.
   * excluding calls known to have reverted. Zero means re-fetching this lender
   * is pointless: the markets in question always revert for this account.
   * Equals `failedCalls` when no `permanentFailures` set was supplied.
   */
  retryableFailedCalls: number
  /** Size of the lender's slice. */
  totalCalls: number
  /** True when nothing was published for this lender. */
  dropped: boolean
  /** What went wrong. */
  reason: IncompleteReason
  /** Extra context for logs (converter message, violation details). */
  detail?: string
}

export interface ConvertLenderUserDataOptions {
  /** Invoked once per lender that did not convert cleanly — for logging / surfacing. */
  onIncomplete?: (info: IncompleteLenderRead) => void
  /**
   * Indices (into `rawResults`) of calls that failed deterministically, as
   * collected by `getLenderUserDataResult`. Used only to compute
   * `retryableFailedCalls`.
   */
  permanentFailures?: Set<number>
}

/**
 * Converts the raw results into the desired format
 *
 * Slots that hold the multicall failure sentinel are NOT data: a failed read
 * says nothing about the user's position. Coercing one to zero is how a
 * rate-limited RPC ends up rendering phantom $0 rows and understated balances,
 * so failures are handled explicitly, in three gates:
 *
 *  1. **Failure policy** (see {@link ReadFailurePolicy}). Every read failing
 *     drops the lender under either policy. Beyond that, a `strict`
 *     (cross-margin) lender drops on ANY failure because its aggregates are only
 *     meaningful over the complete set, while a `lenient` (multi-market) lender
 *     publishes the markets that did read and is flagged `incomplete`.
 *  2. **Converter errors** are reported rather than swallowed — a throwing
 *     converter used to leave a lender silently absent, indistinguishable from a
 *     user with no position there.
 *  3. **Invariant validation** (see `validate.ts`) rejects sub-accounts that
 *     cannot be true regardless of how the reads went — a `NaN` anywhere in the
 *     aggregates, or (alongside failed reads) debt with no collateral behind it.
 *
 * Anything published after that is either complete or explicitly marked as not.
 *
 * @param chainId - The chain ID
 * @param queriesRaw - The queries to fetch data for
 * @param rawResults - The raw results from the multicall
 * @param lenderState - The state of the lender
 * @param options - Optional reporting hooks
 * @returns The converted data
 */
export const convertLenderUserDataResult = (
  chainId: string,
  queriesRaw: LenderUserQuery[],
  rawResults: any[],
  lenderState: LenderToLenderCrossPoolMeta,
  options?: ConvertLenderUserDataOptions,
): { [lender: string]: UserData } => {
  const queries = organizeUserQueries(queriesRaw)
  const lenderData: {
    [lender: string]: UserData
  } = {}
  let currentSlice = 0
  for (const query of queries) {
    const [converter, sliceLength] = getUserDataConverter(
      query.lender,
      chainId,
      query.account,
      query.params,
      lenderState,
    )
    const data = rawResults.slice(currentSlice, currentSlice + sliceLength)

    let failedCalls = 0
    let retryableFailedCalls = 0
    for (let i = 0; i < data.length; i++) {
      if (!isFailedCall(data[i])) continue
      failedCalls++
      if (!options?.permanentFailures?.has(currentSlice + i))
        retryableFailedCalls++
    }
    currentSlice += sliceLength

    const allFailed = data.length > 0 && failedCalls === data.length
    const policy = getReadFailurePolicy(query.lender)

    // Gate 1: failure policy. A strict lender publishes nothing once ANY read is
    // missing — see `ReadFailurePolicy` for why a partial cross-margin set is
    // wrong rather than incomplete. Only RETRYABLE failures count: a market that
    // deterministically reverts for this account is an answer ("no position"),
    // not a gap, and must not void an otherwise-clean read forever.
    const voided =
      allFailed || (policy === 'strict' && retryableFailedCalls > 0)

    if (failedCalls > 0) {
      options?.onIncomplete?.({
        lender: query.lender,
        failedCalls,
        retryableFailedCalls,
        totalCalls: data.length,
        dropped: voided,
        reason: allFailed
          ? 'all-reads-failed'
          : voided
            ? 'partial-read-cross-margin'
            : 'partial-read',
      })
    }
    if (voided) continue

    // Gate 2: converter errors are reported, never swallowed.
    let convertedData: any
    try {
      convertedData = converter(data)
    } catch (e: any) {
      options?.onIncomplete?.({
        lender: query.lender,
        failedCalls,
        retryableFailedCalls,
        totalCalls: data.length,
        dropped: true,
        reason: 'converter-error',
        detail: e instanceof Error ? e.message : String(e),
      })
      continue
    }
    if (!convertedData) continue

    const incomplete = retryableFailedCalls > 0

    // Gate 3: invariant validation, applied to every published entry.
    const publish = (key: string, entry: UserData | null) => {
      if (!entry) return
      const { kept, violations, dropped } = validateUserData(entry, incomplete)
      if (dropped.length > 0) {
        options?.onIncomplete?.({
          lender: key,
          failedCalls,
          retryableFailedCalls,
          totalCalls: data.length,
          dropped: kept.length === 0,
          reason: 'invariant-violation',
          detail: violations.map((v) => `${v.code}: ${v.detail}`).join('; '),
        })
      }
      if (kept.length === 0) return
      entry.data = kept
      entry.lender = key
      if (incomplete) entry.incomplete = true
      lenderData[key] = entry
    }

    try {
      if (isMultiMarket(query.lender)) {
        for (const market of Object.keys(convertedData)) {
          publish(market, filterEmptyUserData(convertedData[market]))
        }
      } else {
        publish(
          query.lender,
          filterEmptyUserData(
            convertedData as UserData,
            // Euler: retain a controller-only (empty) sub-account so its active
            // controller mode reaches the leverage-open builder.
            isEulerType(query.lender),
          ),
        )
      }
    } catch (e: any) {
      options?.onIncomplete?.({
        lender: query.lender,
        failedCalls,
        retryableFailedCalls,
        totalCalls: data.length,
        dropped: true,
        reason: 'converter-error',
        detail: e instanceof Error ? e.message : String(e),
      })
    }
  }
  return lenderData
}
